// @wander/shared — Profile context builder for LLM prompts
// Ported from web's WanderAppComplete.jsx buildProfileContext()

import { companionsToPromptString } from './preferenceSchema.js';

/**
 * Build a profile context string for LLM generation prompts.
 * Includes user preferences, accessibility needs, safety considerations, etc.
 * Omits sections with no data.
 *
 * @param {Object} params
 * @param {string} [params.homeCity]
 * @param {string} [params.budget] — 'budget' | 'mid-range' | 'luxury'
 * @param {string[]} [params.interests]
 * @param {string[]} [params.dietary] — dietary restrictions
 * @param {string[]} [params.accessibility] — accessibility needs
 * @param {string} [params.pace] — preferred travel pace
 * @param {string} [params.companions] — travel companions description
 * @param {string} [params.nationality]
 * @param {string[]} [params.safetyConsiderations] — e.g. ['lgbtq', 'solo_female']
 * @param {string} [params.travelNotes] — free-form travel notes
 * @param {string} [params.cabinClass] — 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST'
 * @param {string} [params.accommodationType] — 'hotel' | 'cruise' | 'campervan' | 'custom'
 * @param {string} [params.customAccommodationName]
 * @param {string[]} [params.likedActivities]
 * @param {string[]} [params.dislikedActivities]
 * @param {string[]} [params.likedCuisines]
 * @param {string[]} [params.dislikedCuisines]
 * @returns {string} — formatted profile context block, or empty string if no data
 */
export const buildProfileContext = ({
  homeCity,
  budget,
  interests,
  dietary,
  accessibility,
  pace,
  companions,
  nationality,
  safetyConsiderations,
  travelNotes,
  cabinClass,
  accommodationType,
  customAccommodationName,
  likedActivities,
  dislikedActivities,
  likedCuisines,
  dislikedCuisines,
  tasteProfile,
} = {}) => {
  const lines = [];

  if (homeCity) lines.push(`Home city: ${homeCity}`);

  if (budget) {
    const budgetTier = (typeof budget === 'string' ? budget : '').toLowerCase();
    lines.push(`Budget: ${budget}`);
    if (budgetTier === 'budget') {
      lines.push(`BUDGET TIER: Hostels/guesthouses/budget hotels. Buses, budget airlines, public transit. Street food, markets, local cafes. Free walking tours, parks, free museums. Include approximate costs.`);
    } else if (budgetTier === 'luxury') {
      lines.push(`LUXURY TIER: 5-star hotels, luxury resorts. Private transfers, business/first class. Fine dining, Michelin-starred restaurants. Private tours, VIP access, exclusive experiences.`);
    }
  }

  if (interests?.length > 0) lines.push(`Interests: ${interests.join(', ')}`);
  if (dietary?.length > 0) lines.push(`Dietary restrictions: ${dietary.join(', ')}`);

  if (accessibility?.length > 0) {
    lines.push(`Accessibility needs (MUST accommodate): ${accessibility.join(', ')}`);
    if (accessibility.includes('Wheelchair accessible')) {
      lines.push(`ALL venues MUST be wheelchair accessible. Note step-free access. Avoid cobblestones/stairs.`);
    }
    if (accessibility.includes('Limited mobility')) {
      lines.push(`Max 3-4 activities/day. Include seated rest breaks. Avoid long walks (>1km without rest).`);
    }
    if (accessibility.includes('Service animal')) {
      lines.push(`ALL venues must accept service animals.`);
    }
  }

  if (pace) lines.push(`Travel pace: ${pace}`);
  // companions can arrive as string OR array — coerce to a readable phrase.
  const companionsLine = companionsToPromptString(companions, '');
  if (companionsLine) lines.push(`Usually travels: ${companionsLine}`);

  // Travel class context
  if (cabinClass === 'FIRST' || cabinClass === 'BUSINESS') {
    lines.push(`Flying ${cabinClass} class — traveler arrives well-rested even on long-haul flights. Can schedule lighter activities on arrival day but don't need extended recovery time.`);
  }

  // Accommodation type context
  if (accommodationType === 'cruise') {
    lines.push(`CRUISE ACCOMMODATION: Ship is the hotel. Plan shore excursion activities for port days only. User must return to ship by departure time. All meals can be on the ship — only include notable local restaurants as optional.`);
  } else if (accommodationType === 'campervan') {
    lines.push(`CAMPERVAN ACCOMMODATION: User travels by campervan, no hotel needed. Suggest campsite/overnight parking spots. Include morning pack-up and evening setup routines.`);
  } else if (accommodationType === 'custom' && customAccommodationName) {
    lines.push(`CUSTOM ACCOMMODATION: "${customAccommodationName}" — use this name throughout instead of "hotel". Do NOT suggest alternative hotels.`);
  }

  // Safety considerations
  if (safetyConsiderations?.length > 0) {
    const safetyLabels = {
      lgbtq: 'LGBTQ+ friendly',
      solo_female: 'Solo female traveler safety',
      religious: 'Religious/cultural considerations'
    };
    const labels = safetyConsiderations.map(s => safetyLabels[s] || s);
    lines.push(`Safety priorities: ${labels.join(', ')}`);
  }

  if (travelNotes) lines.push(`Travel notes: "${travelNotes}"`);

  // Accumulated feedback from past trips
  if (likedActivities?.length > 0) lines.push(`Activities they enjoy: ${likedActivities.slice(-10).join(', ')}`);
  if (dislikedActivities?.length > 0) lines.push(`Activities to AVOID: ${dislikedActivities.slice(-10).join(', ')}`);
  if (likedCuisines?.length > 0) lines.push(`Preferred cuisines: ${likedCuisines.slice(-8).join(', ')}`);
  if (dislikedCuisines?.length > 0) lines.push(`Cuisines to avoid: ${dislikedCuisines.slice(-8).join(', ')}`);

  // Taste profile from community reviews (Beli-style)
  if (tasteProfile) {
    if (tasteProfile.travel_personality) {
      lines.push(`Travel personality: ${tasteProfile.travel_personality}`);
    }
    const topCuisines = tasteProfile.cuisine_preferences
      ? Object.entries(tasteProfile.cuisine_preferences)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name]) => name)
      : [];
    if (topCuisines.length > 0) {
      lines.push(`Favorite cuisines (from reviews): ${topCuisines.join(', ')} — prioritize these for meal suggestions`);
    }
    const topActivities = tasteProfile.activity_preferences
      ? Object.entries(tasteProfile.activity_preferences)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name]) => name)
      : [];
    if (topActivities.length > 0) {
      lines.push(`Favorite activities (from reviews): ${topActivities.join(', ')} — prioritize these types`);
    }
  }

  if (lines.length === 0) return '';
  return `\nUSER PROFILE (tailor all suggestions to these preferences):\n- ${lines.join('\n- ')}`;
};

/**
 * Build feedback context from user preferences and past trip feedback.
 * More detailed than buildProfileContext — includes per-accessibility-need
 * instructions and separates profile prefs from learned feedback.
 *
 * @param {Object} prefs - User preferences object
 * @returns {string} - Formatted feedback context block, or empty string if no data
 */
export function buildFeedbackContext(prefs) {
  const profileSections = [];
  const feedbackSections = [];

  // --- Profile preferences (from Settings > Travel) ---
  if (prefs?.interests?.length > 0) {
    profileSections.push(`- Interests: ${prefs.interests.join(', ')}`);
  }
  if (prefs?.accessibilityNeeds?.length > 0) {
    const accessibilityDetails = [];
    accessibilityDetails.push(`ACCESSIBILITY REQUIREMENTS (MUST accommodate): ${prefs.accessibilityNeeds.join(', ')}`);
    if (prefs.accessibilityNeeds.includes('Wheelchair accessible')) {
      accessibilityDetails.push(`WHEELCHAIR: ALL venues must be wheelchair accessible. Note step-free access, elevators, ramps. Avoid stairs, cobblestones, uneven terrain. Suggest accessible taxis, not standard buses. Hotels must have accessible rooms.`);
    }
    if (prefs.accessibilityNeeds.includes('Visual impairment')) {
      accessibilityDetails.push(`VISUAL IMPAIRMENT: Suggest audio-described tours and tactile exhibits. Note if museums offer audio guides. Hotels must accept guide dogs. Prefer well-lit, clearly marked routes.`);
    }
    if (prefs.accessibilityNeeds.includes('Limited mobility')) {
      accessibilityDetails.push(`LIMITED MOBILITY: Maximum 3-4 activities per day. Ensure rest breaks between activities. Avoid long walking distances (>1km without rest). Note steep hills, stairs, elevation changes. Suggest transport between nearby attractions.`);
    }
    if (prefs.accessibilityNeeds.includes('Hearing impairment')) {
      accessibilityDetails.push(`HEARING IMPAIRMENT: Note venues with visual displays, subtitled presentations, or sign language interpretation. Suggest visual-focused experiences.`);
    }
    if (prefs.accessibilityNeeds.includes('Service animal')) {
      accessibilityDetails.push(`SERVICE ANIMAL: ALL hotels, restaurants, and attractions must accept service animals. Note pet-friendly outdoor spaces. Verify transport services accept service animals.`);
    }
    profileSections.push(`- ${accessibilityDetails.join('\n  ')}`);
  }
  if (prefs?.safetyConsiderations?.length > 0) {
    const safetyLabels = {
      lgbtq: 'LGBTQ+ friendly destinations',
      solo_female: 'Solo female traveler safety',
      religious: 'Religious/cultural considerations'
    };
    const labels = prefs.safetyConsiderations.map(s => safetyLabels[s] || s);
    profileSections.push(`- Safety priorities: ${labels.join(', ')}`);
  }
  if (prefs?.safetyNotes) {
    profileSections.push(`- Safety notes: ${prefs.safetyNotes}`);
  }
  // companions: handle both single-string (legacy) and array (canonical)
  {
    const phrase = companionsToPromptString(prefs?.travelCompanions ?? prefs?.companions, '');
    if (phrase) profileSections.push(`- Usually travels: ${phrase}`);
  }
  if (prefs?.travelNotes) {
    profileSections.push(`- Personal travel notes: "${prefs.travelNotes}"`);
  }

  // --- Accumulated feedback (from past trip thumbs up/down) ---
  if (prefs?.likedActivities?.length > 0) {
    feedbackSections.push(`- Activities they've enjoyed: ${prefs.likedActivities.slice(-15).join(', ')}`);
  }
  if (prefs?.dislikedActivities?.length > 0) {
    feedbackSections.push(`- Activities they've disliked (AVOID similar): ${prefs.dislikedActivities.slice(-15).join(', ')}`);
  }
  if (prefs?.likedCuisines?.length > 0) {
    feedbackSections.push(`- Cuisines they prefer: ${prefs.likedCuisines.slice(-10).join(', ')}`);
  }
  if (prefs?.dislikedCuisines?.length > 0) {
    feedbackSections.push(`- Cuisines to avoid: ${prefs.dislikedCuisines.slice(-10).join(', ')}`);
  }
  if (prefs?.preferredNeighborhoods?.length > 0) {
    feedbackSections.push(`- Neighborhood styles they prefer: ${prefs.preferredNeighborhoods.slice(-10).join(', ')}`);
  }

  const allSections = [];
  if (profileSections.length > 0) {
    allSections.push(`USER PROFILE (prioritize these in all recommendations):\n${profileSections.join('\n')}`);
  }
  if (feedbackSections.length > 0) {
    allSections.push(`LEARNED PREFERENCES (from past trips - adapt accordingly):\n${feedbackSections.join('\n')}`);
  }

  if (allSections.length === 0) return '';

  return `\n${allSections.join('\n\n')}\n`;
}
